<template>
  <v-card outlined>
    <v-container class="px-1 py-1" fluid>
      <v-card-title class="pt-2 pb-0">
        {{ $t('ELE0028') }}
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <PartCodeInput v-if="isEpson" v-model="searchCondition.partCd" />
            <SupplierPartInput v-else v-model="searchCondition.partCd" :supplierCd="''" isSearchByLoginCompany />
          </v-col>
          <v-col>
            <CompanyInput
              v-if="isEpson"
              :label="$t('ELE0134')"
              v-model="searchCondition.manufacturerCd"
              :companyCatCd="enumCompanyCatCd.SUPPLIER"
            />
            <ManufacturerInput v-else :label="$t('ELE0134')" v-model="searchCondition.manufacturerCd" />
          </v-col>
          <v-col>
            <XrfGroupInput v-model="searchCondition.xrfGroupCd" />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <TextFieldInputWithDatePicker
              v-model="searchCondition.expirationDateFrom"
              :label="$t('ELE0254')"
              clearable
            />
          </v-col>
          <span class="d-flex flex-row space-between mx-4 mt-3 pt-2">
            {{ $t('ELE0141') }}
          </span>
          <v-col>
            <TextFieldInputWithDatePicker v-model="searchCondition.expirationDateTo" :label="$t('ELE0254')" clearable />
          </v-col>
          <v-col>
            <span class="d-flex flex-row space-between mr-4">
              <Checkbox :label="$t('ELE0522')" v-model="searchCondition.needXrfInspOnly" />
            </span>
          </v-col>
          <v-col class="pt-0 d-flex justify-end align-center flex-grow-0">
            <BtnSearch @click="onClickSearch" :disabled="isFieldAllEmpty" />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card outlined>
        <v-card-title>
          {{ $t('ELE0023') }}
          <v-spacer />
          <BtnAdd :label="$t('ELE0528')" @click="onClickAddBasicInfo" />
        </v-card-title>
        <v-data-table :headers="headers" :items="items" dense>
          <template v-slot:item="{ item }">
            <tr :style="item.needXrf ? 'background-color: #F8CED2' : ''">
              <td class="v-data-table__divider"><IconInquiry @click="onClickXrfHistory(item)" /></td>
              <td class="v-data-table__divider"><IconEdit @click="onClickEditBasicInfo(item)" /></td>
              <td class="v-data-table__divider"><IconEdit @click="onClickInspResult(item)" /></td>
              <td class="v-data-table__divider">{{ item.part }}</td>
              <td class="v-data-table__divider">{{ item.supplier }}</td>
              <td class="v-data-table__divider text-right">{{ item.inspCount }}</td>
              <td class="v-data-table__divider">{{ item.xrfInspDate }}</td>
              <td class="v-data-table__divider text-right">{{ item.xrfInspCycle }}</td>
              <td class="v-data-table__divider">{{ item.xrfExpiredDate }}</td>
              <td class="v-data-table__divider">{{ item.xrfGroupName }}</td>
              <td class="v-data-table__divider">{{ item.repInspPart }}</td>
              <td class="v-data-table__divider">{{ $t('IsRequiredXrf.' + item.isRequiredXrf) }}</td>
              <td class="v-data-table__divider text-right">{{ item.inspQty }}</td>
              <td class="v-data-table__divider">{{ item.inspDept }}</td>
              <td class="v-data-table__divider">
                {{ item.processCd ? `${item.processCd} : ${$t('XrfPostProcessingCd.' + item.processCd)}` : '' }}
              </td>
              <td>{{ $t('isValid.' + item.isValid) }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
    <XrfBasicInfoDialog
      v-if="basicInfoDialog"
      :mode="basicInfoItem.mode"
      :dialog.sync="basicInfoDialog"
      :partCd.sync="basicInfoItem.partCd"
      :manufacturerCd.sync="basicInfoItem.manufacturerCd"
      :xrfGroupCd.sync="basicInfoItem.xrfGroupCd"
      :isRequiredXrf.sync="basicInfoItem.isRequiredXrf"
      :xrfInspCycle.sync="basicInfoItem.xrfInspCycle"
      :inspQty.sync="basicInfoItem.inspQty"
      :inspDeptCd.sync="basicInfoItem.inspDeptCd"
      :processCd.sync="basicInfoItem.processCd"
      :isValid.sync="basicInfoItem.isValid"
      :versionNo="basicInfoItem.versionNo"
      @change="onClickSearch"
    />
    <XrfInspResultDialog
      v-if="inspResultDialog"
      :dialog.sync="inspResultDialog"
      :partCd="inspResultItem.partCd"
      :manufacturerCd="inspResultItem.manufacturerCd"
      :xrfGroupCd="inspResultItem.xrfGroupCd"
      :xrfInspCycle="inspResultItem.xrfInspCycle"
      @change="onClickSearch"
    />
    <XrfHistoryDialog
      v-if="xrfHistoryDialog"
      :dialog.sync="xrfHistoryDialog"
      :part="xrfHistoryItem.part"
      :supplier="xrfHistoryItem.supplier"
      :partCd="xrfHistoryItem.partCd"
      :manufacturerCd="xrfHistoryItem.manufacturerCd"
    />
  </v-card>
</template>

<script>
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtils from '@/mixin/date-utils.js'
import StringUtils from '@/mixin/string-utils'
import { CompanyCatCd, ModalMode } from '@/enum.js'

import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'
import BtnSearch from '@/components/atoms/button/BtnSearch'
import BtnAdd from '@/components/atoms/button/BtnAdd'
import IconEdit from '@/components/atoms/icon/IconEdit'
import IconInquiry from '@/components/atoms/icon/IconInquiry'
import Checkbox from '@/components/atoms/base/checkbox/Checkbox'

import PartCodeInput from '@/components/atoms/select/PartCodeInput'
import CompanyInput from '@/components/atoms/select/CompanyInput'
import SupplierPartInput from '@/components/atoms/select/SupplierPartInput'
import ManufacturerInput from '@/components/atoms/select/ManufacturerInput'
import XrfGroupInput from '@/components/atoms/select/XrfGroupInput'

import XrfBasicInfoDialog from '@/components/organisms/terminal/dialog/XrfBasicInfoDialog'
import XrfInspResultDialog from '@/components/organisms/terminal/dialog/XrfInspResultDialog'
import XrfHistoryDialog from '@/components/organisms/terminal/dialog/XrfHistoryDialog'

export default {
  name: 'InputXrfInspForm',

  mixins: [APIUtils, Message, Loading, DateUtils, StringUtils],

  components: {
    TextFieldInputWithDatePicker,
    BtnSearch,
    BtnAdd,
    IconEdit,
    IconInquiry,
    Checkbox,
    PartCodeInput,
    CompanyInput,
    SupplierPartInput,
    ManufacturerInput,
    XrfGroupInput,
    XrfBasicInfoDialog,
    XrfInspResultDialog,
    XrfHistoryDialog,
  },

  data() {
    return {
      basicInfoDialog: false,
      basicInfoItem: null,
      inspResultDialog: false,
      inspResultItem: null,
      xrfHistoryDialog: false,
      xrfHistoryItem: null,
      searchCondition: {
        partCd: '',
        manufacturerCd: '',
        xrfGroupCd: '',
        expirationDateFrom: '',
        expirationDateTo: '',
        needXrfInspOnly: false,
      },
      headers: [
        { text: this.$t('ELE0504'), value: 'history', sortable: false },
        { text: this.$t('ELE0523'), value: 'basicInfo', sortable: false },
        { text: this.$t('ELE0188'), value: 'inspResult', sortable: false },
        { text: this.$t('ELE0059'), value: 'part' },
        { text: this.$t('ELE0134'), value: 'supplier' },
        { text: this.$t('ELE0098'), value: 'inspCount' },
        { text: this.$t('ELE0138'), value: 'xrfInspDate' },
        { text: this.$t('ELE0530'), value: 'xrfInspCycle' },
        { text: this.$t('ELE0254'), value: 'xrfExpiredDate' },
        { text: this.$t('ELE0519'), value: 'xrfGroupName' },
        { text: this.$t('ELE0525'), value: 'repInspPart' },
        { text: this.$t('ELE0524'), value: 'isRequiredXrf' },
        { text: this.$t('ELE0532'), value: 'inspQty' },
        { text: this.$t('ELE0533'), value: 'inspDept' },
        { text: this.$t('ELE0534'), value: 'processCd' },
        { text: this.$t('ELE0014'), value: 'isValid' },
      ],
      items: [],
    }
  },

  computed: {
    enumCompanyCatCd: () => CompanyCatCd,
    isEpson: {
      get() {
        return this.$store.getters.isEpson
      },
    },
    isFieldAllEmpty: {
      get() {
        return this.isAllEmpty(
          this.searchCondition.partCd,
          this.searchCondition.manufacturerCd,
          this.searchCondition.xrfGroupCd,
          this.searchCondition.expirationDateFrom,
          this.searchCondition.expirationDateTo,
          this.searchCondition.needXrfInspOnly,
        )
      },
    },
  },

  methods: {
    onClickAddBasicInfo() {
      this.basicInfoItem = {
        mode: ModalMode.ADD,
        partCd: '',
        manufacturerCd: '',
        xrfGroupCd: '',
        isRequiredXrf: false,
        xrfInspCycle: '',
        inspQty: '',
        inspDeptCd: '',
        processCd: '',
        isValid: true,
        versionNo: 0,
      }
      this.basicInfoDialog = true
    },
    onClickXrfHistory(item) {
      this.xrfHistoryItem = {
        part: item.part,
        supplier: item.supplier,
        partCd: item.partCd,
        manufacturerCd: item.manufacturerCd,
      }
      this.xrfHistoryDialog = true
    },
    onClickEditBasicInfo(item) {
      this.basicInfoItem = {
        mode: ModalMode.EDIT,
        partCd: item.partCd,
        manufacturerCd: item.manufacturerCd,
        xrfGroupCd: item.xrfGroupCd !== null ? item.xrfGroupCd : '',
        isRequiredXrf: item.isRequiredXrf,
        // TextFieldの都合上、数値->文字列へCastする
        // isRequiredXrf=Trueの場合Nullであるため、その対策も追加
        xrfInspCycle: item.isRequiredXrf ? '' : String(item.xrfInspCycle),
        inspQty: item.inspQty,
        inspDeptCd: item.inspDeptCd,
        processCd: item.processCd,
        isValid: item.isValid,
        versionNo: item.versionNo,
      }
      this.basicInfoDialog = true
    },
    onClickInspResult(item) {
      this.inspResultItem = {
        partCd: item.partCd,
        manufacturerCd: item.manufacturerCd,
        xrfGroupCd: item.xrfGroupCd !== null ? item.xrfGroupCd : '',
        // TextFieldの都合上、数値->文字列へCastする
        xrfInspCycle: item.isRequiredXrf ? '' : String(item.xrfInspCycle),
      }
      this.inspResultDialog = true
    },
    async onClickSearch() {
      try {
        this.startLoading()
        // 検索条件が入っていない場合、基礎情報追加後等も検索しない
        if (this.isFieldAllEmpty) return
        const params = {
          userCompanyCd: this.$store.getters.companyCd,
          partCd: this.searchCondition.partCd,
          manufacturerCd: this.searchCondition.manufacturerCd,
          xrfGroupCd: this.searchCondition.xrfGroupCd,
          expirationDateFrom: this.searchCondition.expirationDateFrom,
          expirationDateTo: this.searchCondition.expirationDateTo,
          needXrfInspOnly: this.searchCondition.needXrfInspOnly,
        }
        const elemetns = await API.get('/xrfs', params)
        this.items = elemetns.map((e) => {
          e['part'] = `${e.partCd} : ${e.partName}`
          e['supplier'] = `${e.manufacturerCd} : ${e.manufacturerName}`
          e['repInspPart'] = e.repInspPartCd ? `${e.repInspPartCd} : ${e.repInspPartName}` : ''
          e['inspDept'] = e.inspDeptCd ? `${e.inspDeptCd} : ${e.inspDeptName}` : ''
          return e
        })
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0529') })))
      } finally {
        this.stopLoading()
      }
    },
  },

  mounted() {},
}
</script>
